/**
 * PocketFiler - Email Verification Banner
 * Shows at the top of every authenticated page for users with unverified emails.
 * Includes a "Resend verification" button.
 */

import React, { useState } from 'react';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Mail, X, Loader2, CheckCircle2 } from 'lucide-react';


export default function EmailVerificationBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('emailVerifyBannerDismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  // Don't show if verified, no user, or dismissed
  if (!user) return null;
  if (user.email_verified === true || user.email_verified === undefined) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('emailVerifyBannerDismissed', 'true'); } catch {}
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification', {});
      setSent(true);
      toast.success(t('verifyBanner.sent') || 'Verification email sent! Check your inbox.');
      setTimeout(() => setSent(false), 5000);
    } catch (e) {
      toast.error(t('verifyBanner.error') || 'Failed to send verification email. Try again in a minute.');
    }
    setResending(false);
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900 no-print">
      <div className="container mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-sm">
            <Mail className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-amber-900 dark:text-amber-100">
              <strong>{t('verifyBanner.title') || 'Verify your email'}</strong>
              <span className="hidden sm:inline"> — </span>
              <span className="block sm:inline text-xs sm:text-sm text-amber-800 dark:text-amber-200">
                {t('verifyBanner.message', { email: user.email }) ||
                  `We sent a link to ${user.email}. Click it to unlock all features.`}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResend}
              disabled={resending || sent}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-60 transition-colors"
            >
              {resending ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> {t('verifyBanner.sending') || 'Sending...'}</>
              ) : sent ? (
                <><CheckCircle2 className="h-3 w-3" /> {t('verifyBanner.sentShort') || 'Sent!'}</>
              ) : (
                <>{t('verifyBanner.resend') || 'Resend email'}</>
              )}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1 text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 rounded"
              title="Dismiss for this session"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
